/**
 * Authentication Types (Hub BFF)
 */

export type UserRole = 'engineer' | 'internal_monitor' | 'leadership';

/** Local user identity returned by Hub auth endpoints. */
export interface AuthUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: UserRole;
  /** Display name derived from first/last or email. */
  name: string;
  /** Optional avatar URL when available. */
  picture?: string | null;
}

export interface AuthTokenPayload {
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
    role?: UserRole;
  };
}

export function toAuthUser(user: AuthTokenPayload['user']): AuthUser {
  const first = user.first_name?.trim() || '';
  const last = user.last_name?.trim() || '';
  const name = [first, last].filter(Boolean).join(' ') || user.email;
  return {
    id: user.id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role || 'engineer',
    name,
    picture: null,
  };
}

// Auth State
export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Auth Context Type
export interface AuthContextType extends AuthState {
  /** True when /auth/me reports role leadership. */
  isSuperadmin: boolean;
  /** True for Internal monitor or Leadership. */
  canMonitor: boolean;
  loginWithEmail: (email: string, password: string) => Promise<AuthUser>;
  completeOAuth: (code: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}
